<script setup>
/* global axios */
// Cadastro em MASSA de templates WhatsApp (porta do KanbanCW): monta o template
// uma vez (cabeçalho/corpo/rodapé/botões + preview) e cria em VÁRIAS caixas
// oficiais (Cloud API) selecionadas de uma vez. Resultado por caixa.
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import KanbanSelect from './KanbanSelect.vue';

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const apiBase = `/api/v1/accounts/${accountId}/kanban`;
const base = `${apiBase}/whatsapp_templates`;
// endpoint PADRÃO de inboxes (traz channel_type + provider, ao contrário do
// /kanban/inboxes que devolve só {id,name}).
const inboxesUrl = `/api/v1/accounts/${accountId}/inboxes`;

const inboxes = ref([]);
const selected = ref(new Set());
const results = ref([]);
const saving = ref(false);
const error = ref('');

const officialInboxes = computed(() =>
  inboxes.value.filter(
    i =>
      i.channel_type === 'Channel::Whatsapp' && i.provider === 'whatsapp_cloud'
  )
);
const toggleInbox = id => {
  const s = new Set(selected.value);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  selected.value = s;
};
const allSelected = computed(
  () =>
    officialInboxes.value.length > 0 &&
    officialInboxes.value.every(i => selected.value.has(i.id))
);
const toggleAll = () => {
  selected.value = allSelected.value
    ? new Set()
    : new Set(officialInboxes.value.map(i => i.id));
};

const LANGUAGES = ['pt_BR', 'en_US', 'es_ES', 'en', 'es'];
const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const HEADER_FORMATS = ['none', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];
const MEDIA_HEADER_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];
const ACCEPT_BY_FORMAT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT: 'application/pdf',
};

const freshForm = () => ({
  name: '',
  category: 'MARKETING',
  language: 'pt_BR',
  header_format: 'none',
  header_text: '',
  header_handle: '',
  header_media_name: '',
  header_media_preview: '',
  body: '',
  footer: '',
  buttons: [],
});
const form = ref(freshForm());
const bodyVarExamples = ref({});
const headerVarExamples = ref({});

const extractVarIndices = text => {
  const matches = `${text || ''}`.match(/\{\{(\d+)\}\}/g) || [];
  return [
    ...new Set(matches.map(m => parseInt(m.replace(/\{\{|\}\}/g, ''), 10))),
  ].sort((a, b) => a - b);
};
const bodyVarIndices = computed(() => extractVarIndices(form.value.body));
// monta "{{n}}" em JS — não dá pra escrever literal no template (o Vue interpreta
// o }} interno como fim da interpolação).
const varTag = idx => `{{${idx}}}`;
const insertBodyVar = () => {
  const next = (bodyVarIndices.value[bodyVarIndices.value.length - 1] || 0) + 1;
  form.value.body = `${form.value.body || ''}{{${next}}}`;
};
const fillVars = (text, examples) =>
  `${text || ''}`.replace(
    /\{\{(\d+)\}\}/g,
    (m, n) => examples[n] || examples[Number(n)] || m
  );
const previewBody = computed(() =>
  fillVars(form.value.body, bodyVarExamples.value)
);
const previewHeader = computed(() =>
  fillVars(form.value.header_text, headerVarExamples.value)
);

const isMediaHeader = computed(() =>
  MEDIA_HEADER_FORMATS.includes(form.value.header_format)
);

// opções p/ os KanbanSelect (dropdown custom, no lugar do <select> nativo)
const categoryOptions = CATEGORIES.map(c => ({ value: c, label: c }));
const languageOptions = LANGUAGES.map(l => ({ value: l, label: l }));
const buttonTypeOptions = BUTTON_TYPES.map(b => ({ value: b, label: b }));
const headerOptions = computed(() =>
  HEADER_FORMATS.map(h => ({
    value: h,
    label: t(`DISPARADOR.templates.headerFmt.${h}`),
  }))
);
const uploadingMedia = ref(false);
const mediaError = ref('');
const uploadHeaderMedia = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  const firstInbox = [...selected.value][0];
  if (!firstInbox) {
    mediaError.value = t('DISPARADOR.templates.selectInboxForMedia');
    return;
  }
  uploadingMedia.value = true;
  mediaError.value = '';
  form.value.header_handle = '';
  form.value.header_media_name = file.name;
  form.value.header_media_preview =
    form.value.header_format === 'IMAGE' ? URL.createObjectURL(file) : '';
  try {
    const fd = new FormData();
    fd.append('inbox_id', firstInbox);
    fd.append('file', file);
    const { data } = await axios.post(`${base}/upload_header_media`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    form.value.header_handle = data.handle;
  } catch (e) {
    mediaError.value =
      e?.response?.data?.error || t('DISPARADOR.templates.mediaError');
    form.value.header_media_name = '';
    form.value.header_media_preview = '';
  } finally {
    uploadingMedia.value = false;
  }
};

const addButton = () => {
  if (form.value.buttons.length >= 3) return;
  form.value.buttons.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phone_number: '',
  });
};
const removeButton = i => form.value.buttons.splice(i, 1);

const submit = async () => {
  error.value = '';
  results.value = [];
  if (selected.value.size === 0) {
    error.value = t('DISPARADOR.templates.selectAtLeastOne');
    return;
  }
  if (!form.value.name.trim() || !form.value.body.trim()) {
    error.value = t('DISPARADOR.templates.required');
    return;
  }
  if (isMediaHeader.value && !form.value.header_handle) {
    error.value = t('DISPARADOR.templates.mediaRequired');
    return;
  }
  saving.value = true;
  try {
    const { data } = await axios.post(`${base}/bulk_create`, {
      inbox_ids: [...selected.value],
      ...form.value,
      body_examples: bodyVarExamples.value,
      header_examples: headerVarExamples.value,
    });
    results.value = data.data || [];
  } catch (e) {
    error.value = e?.response?.data?.error || t('DISPARADOR.templates.error');
  } finally {
    saving.value = false;
  }
};

const resetForm = () => {
  form.value = freshForm();
  bodyVarExamples.value = {};
  headerVarExamples.value = {};
  results.value = [];
  error.value = '';
  dupChecked.value = new Set();
  batchResults.value = [];
};

// --- duplicar de um template já existente: escolhe uma caixa, puxa os templates
// dela e preenche este formulário. Depois é só trocar o nome (a Meta não aceita
// dois com o mesmo nome na MESMA caixa) e/ou marcar outras caixas e cadastrar.
const dupInboxId = ref(null);
const dupTemplates = ref([]);
const dupLoadingTpls = ref(false);
const dupChecked = ref(new Set()); // índices marcados p/ duplicar em lote
const batching = ref(false);
const batchResults = ref([]); // [{name, results[]|fail|media}]
const dupInboxOptions = computed(() =>
  officialInboxes.value.map(i => ({ value: i.id, label: i.name }))
);
const loadDupTemplates = async () => {
  dupTemplates.value = [];
  dupChecked.value = new Set();
  batchResults.value = [];
  if (!dupInboxId.value) return;
  dupLoadingTpls.value = true;
  try {
    dupTemplates.value =
      (await axios.get(`${base}?inbox_id=${dupInboxId.value}`)).data.data || [];
  } catch {
    dupTemplates.value = [];
  } finally {
    dupLoadingTpls.value = false;
  }
};

const MEDIA_FMTS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
// Meta devolve o template em `components` (HEADER/BODY/FOOTER/BUTTONS). Converte
// pro payload do /bulk_create. Cabeçalho de MÍDIA não dá pra reaproveitar o handle
// (upload único) → marcado como `_isMedia` p/ pular no lote (duplica individualmente).
const templateToPayload = tpl => {
  const comp = type => (tpl.components || []).find(c => c.type === type);
  const header = comp('HEADER');
  const body = comp('BODY');
  const footer = comp('FOOTER');
  const origButtons = comp('BUTTONS')?.buttons || [];
  // #168/2 — botão FLOW não é reaproveitável no bulk_create (precisa flow_id/flow_action
  // e publicação própria). Antes ele virava QUICK_REPLY silenciosamente; agora marcamos
  // o template p/ PULAR na duplicação em lote (como o cabeçalho de mídia) e avisamos.
  const _hasFlow = origButtons.some(b => (b.type || '').toUpperCase() === 'FLOW');
  const buttons = origButtons.slice(0, 3).map(b => ({
    type: BUTTON_TYPES.includes(b.type) ? b.type : 'QUICK_REPLY',
    text: b.text || '',
    url: b.url || '',
    phone_number: b.phone_number || '',
  }));
  const body_examples = {};
  (body?.example?.body_text?.[0] || []).forEach((v, i) => {
    body_examples[i + 1] = v;
  });
  const header_examples = {};
  (header?.example?.header_text || []).forEach((v, i) => {
    header_examples[i + 1] = v;
  });
  return {
    name: tpl.name || '',
    category: CATEGORIES.includes(tpl.category) ? tpl.category : 'MARKETING',
    language: LANGUAGES.includes(tpl.language) ? tpl.language : 'pt_BR',
    header_format: header?.format || 'none',
    header_text: header?.format === 'TEXT' ? header.text || '' : '',
    header_handle: '',
    body: body?.text || '',
    footer: footer?.text || '',
    buttons,
    body_examples,
    header_examples,
    _isMedia: MEDIA_FMTS.includes(header?.format),
    _hasFlow,
  };
};
// carrega UM template no formulário (pra ajustar antes de cadastrar)
const applyTemplateToForm = tpl => {
  const p = templateToPayload(tpl);
  form.value = {
    name: p.name,
    category: p.category,
    language: p.language,
    header_format: p.header_format,
    header_text: p.header_text,
    header_handle: '',
    header_media_name: '',
    header_media_preview: '',
    body: p.body,
    footer: p.footer,
    buttons: p.buttons,
  };
  bodyVarExamples.value = { ...p.body_examples };
  headerVarExamples.value = { ...p.header_examples };
  results.value = [];
  batchResults.value = [];
  error.value = '';
};

// multi-seleção de templates
const toggleDupTpl = i => {
  const s = new Set(dupChecked.value);
  if (s.has(i)) s.delete(i);
  else s.add(i);
  dupChecked.value = s;
};
const allDupChecked = computed(
  () =>
    dupTemplates.value.length > 0 &&
    dupTemplates.value.every((_, i) => dupChecked.value.has(i))
);
const toggleAllDup = () => {
  dupChecked.value = allDupChecked.value
    ? new Set()
    : new Set(dupTemplates.value.map((_, i) => i));
};

// DUPLICA os templates marcados em TODAS as caixas marcadas (um /bulk_create por template)
const duplicarSelecionados = async () => {
  error.value = '';
  batchResults.value = [];
  results.value = [];
  if (selected.value.size === 0) {
    error.value = t('DISPARADOR.templates.selectAtLeastOne');
    return;
  }
  const idxs = [...dupChecked.value];
  if (!idxs.length) {
    error.value = t('DISPARADOR.templates.dupSelectTpls');
    return;
  }
  batching.value = true;
  const inbox_ids = [...selected.value];
  const out = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const i of idxs) {
    const tpl = dupTemplates.value[i];
    if (!tpl) continue;
    const { _isMedia, _hasFlow, ...payload } = templateToPayload(tpl);
    if (_hasFlow) {
      out.push({ name: tpl.name, flow: true });
      continue;
    }
    if (_isMedia) {
      out.push({ name: tpl.name, media: true });
      continue;
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await axios.post(`${base}/bulk_create`, {
        inbox_ids,
        ...payload,
      });
      out.push({ name: tpl.name, results: data.data || [] });
    } catch (e) {
      out.push({
        name: tpl.name,
        fail: e?.response?.data?.error || t('DISPARADOR.templates.error'),
      });
    }
  }
  batchResults.value = out;
  batching.value = false;
};
watch(dupInboxId, loadDupTemplates);

onMounted(async () => {
  try {
    inboxes.value = (await axios.get(inboxesUrl)).data.payload || [];
  } catch (e) {
    inboxes.value = [];
  }
});
</script>

<template>
  <!-- Raiz preenche a altura e deixa o MIOLO rolar. O Wrapper das configurações
       aplica `overflow-hidden` no router-view; sem um container de scroll próprio o
       conteúdo (muitas caixas + lista de resultados) ficava cortado, sem rolagem. -->
  <div class="flex flex-col h-full">
    <div class="flex-1 min-h-0 overflow-y-auto">
      <div class="p-6 mx-auto max-w-5xl w-full">
        <p class="mb-4 text-sm text-n-slate-11">
          {{ t('DISPARADOR.templates.subtitle') }}
        </p>

    <div
      v-if="!officialInboxes.length"
      class="py-8 text-sm text-center text-n-slate-10"
    >
      {{ t('DISPARADOR.templates.noOfficial') }}
    </div>

    <div v-else class="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <!-- coluna form -->
      <div class="space-y-4 lg:col-span-2">
        <!-- caixas -->
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.inboxes')
            }}</label>
            <button
              type="button"
              class="text-xs text-n-brand hover:underline"
              @click="toggleAll"
            >
              {{
                allSelected
                  ? t('DISPARADOR.templates.none')
                  : t('DISPARADOR.templates.all')
              }}
            </button>
          </div>
          <div class="flex flex-wrap gap-2">
            <label
              v-for="ib in officialInboxes"
              :key="ib.id"
              class="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border cursor-pointer"
              :class="
                selected.has(ib.id)
                  ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                  : 'border-n-weak text-n-slate-11'
              "
            >
              <input
                type="checkbox"
                :checked="selected.has(ib.id)"
                @change="toggleInbox(ib.id)"
              />
              {{ ib.name }}
            </label>
          </div>
        </div>

        <!-- duplicar de templates existentes (1 ou vários → caixas marcadas) -->
        <div class="p-3 rounded-lg border border-dashed border-n-weak bg-n-alpha-1">
          <label class="block mb-1.5 text-sm font-medium text-n-slate-12">{{
            t('DISPARADOR.templates.dupTitle')
          }}</label>
          <KanbanSelect
            v-model="dupInboxId"
            :options="dupInboxOptions"
            :placeholder="t('DISPARADOR.templates.dupPickInbox')"
          />

          <div
            v-if="dupInboxId && dupLoadingTpls"
            class="mt-2 text-xs text-n-slate-10"
          >
            {{ t('DISPARADOR.templates.dupLoading') }}
          </div>
          <div
            v-else-if="dupInboxId && !dupTemplates.length"
            class="mt-2 text-xs text-n-slate-10"
          >
            {{ t('DISPARADOR.templates.dupEmpty') }}
          </div>
          <div v-else-if="dupInboxId" class="mt-2">
            <div class="flex items-center justify-between mb-1">
              <label
                class="inline-flex items-center gap-2 text-xs cursor-pointer text-n-slate-11"
              >
                <input
                  type="checkbox"
                  :checked="allDupChecked"
                  @change="toggleAllDup"
                />
                {{ t('DISPARADOR.templates.dupAll') }}
              </label>
              <span class="text-[0.6875rem] text-n-slate-10"
                >{{ dupChecked.size }} / {{ dupTemplates.length }}</span
              >
            </div>
            <div
              class="overflow-y-auto border rounded-lg max-h-44 border-n-weak divide-y divide-n-weak"
            >
              <div
                v-for="(tpl, i) in dupTemplates"
                :key="i"
                class="flex items-center gap-2 px-2 py-1.5"
              >
                <input
                  type="checkbox"
                  :checked="dupChecked.has(i)"
                  @change="toggleDupTpl(i)"
                />
                <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12"
                  >{{ tpl.name }}
                  <span class="text-n-slate-10">({{ tpl.language }})</span></span
                >
                <button
                  type="button"
                  class="text-xs shrink-0 text-n-brand hover:underline"
                  @click="applyTemplateToForm(tpl)"
                >
                  {{ t('DISPARADOR.templates.dupEditOne') }}
                </button>
              </div>
            </div>
            <button
              class="px-4 py-2 mt-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
              :disabled="batching || !dupChecked.size || !selected.size"
              @click="duplicarSelecionados"
            >
              {{
                batching
                  ? t('DISPARADOR.templates.saving')
                  : t('DISPARADOR.templates.dupDoBatch', {
                      n: dupChecked.size,
                      m: selected.size,
                    })
              }}
            </button>
          </div>
          <p class="mt-1 text-[0.6875rem] text-n-slate-10">
            {{ t('DISPARADOR.templates.dupHint') }}
          </p>

          <!-- resultados do lote -->
          <div v-if="batchResults.length" class="mt-3 space-y-1">
            <div v-for="(r, ri) in batchResults" :key="ri" class="text-xs">
              <span v-if="r.media" class="text-n-amber-11"
                >⚠ {{ r.name }} — {{ t('DISPARADOR.templates.dupMediaSkip') }}</span
              >
              <span v-else-if="r.flow" class="text-n-amber-11"
                >⚠ {{ r.name }} — {{ t('DISPARADOR.templates.dupFlowSkip') }}</span
              >
              <span v-else-if="r.fail" class="text-n-ruby-11"
                >✕ {{ r.name }} — {{ r.fail }}</span
              >
              <span v-else>
                <span class="font-medium text-n-slate-12">{{ r.name }}</span>:
                <span class="text-n-teal-11"
                  >{{ r.results.filter(x => x.success).length }}
                  {{ t('DISPARADOR.templates.dupCreated') }}</span
                >
                <span
                  v-if="r.results.filter(x => !x.success).length"
                  class="text-n-ruby-11"
                >
                  ·
                  {{ r.results.filter(x => !x.success).length }}
                  {{ t('DISPARADOR.templates.dupFailed') }}:
                  {{
                    r.results
                      .filter(x => !x.success)
                      .map(x => x.inbox_name)
                      .join(', ')
                  }}
                </span>
              </span>
            </div>
          </div>
        </div>

        <!-- nome / categoria / idioma -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.name')
            }}</label>
            <input
              v-model="form.name"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              :placeholder="t('DISPARADOR.templateNamePlaceholder')"
            />
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.category')
            }}</label>
            <KanbanSelect v-model="form.category" :options="categoryOptions" />
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.language')
            }}</label>
            <KanbanSelect v-model="form.language" :options="languageOptions" />
          </div>
        </div>

        <!-- cabeçalho -->
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('DISPARADOR.templates.header')
          }}</label>
          <div class="mb-2">
            <KanbanSelect
              v-model="form.header_format"
              :options="headerOptions"
            />
          </div>
          <input
            v-if="form.header_format === 'TEXT'"
            v-model="form.header_text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            :placeholder="t('DISPARADOR.templates.headerText')"
          />
          <div v-else-if="isMediaHeader">
            <input
              type="file"
              :accept="ACCEPT_BY_FORMAT[form.header_format]"
              @change="uploadHeaderMedia"
            />
            <span v-if="uploadingMedia" class="ml-2 text-xs text-n-slate-10">{{
              t('DISPARADOR.templates.uploading')
            }}</span>
            <span
              v-else-if="form.header_handle"
              class="ml-2 text-xs text-n-teal-11"
              >✓ {{ form.header_media_name }}</span
            >
            <p v-if="mediaError" class="mt-1 text-xs text-n-ruby-11">
              {{ mediaError }}
            </p>
            <p class="mt-1 text-[0.6875rem] text-n-slate-10">
              {{ t('DISPARADOR.templates.mediaHint') }}
            </p>
          </div>
        </div>

        <!-- corpo -->
        <div>
          <div class="flex items-center justify-between mb-1">
            <label class="text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.body')
            }}</label>
            <button
              type="button"
              class="text-xs text-n-brand hover:underline"
              @click="insertBodyVar"
            >
              + {{ t('DISPARADOR.templates.addVar') }}
            </button>
          </div>
          <textarea
            v-model="form.body"
            rows="4"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            :placeholder="t('DISPARADOR.templates.bodyPlaceholder')"
          />
          <div v-if="bodyVarIndices.length" class="mt-2 space-y-1">
            <div
              v-for="i in bodyVarIndices"
              :key="`b${i}`"
              class="flex items-center gap-2"
            >
              <span class="text-xs text-n-slate-10 w-14">{{ varTag(i) }}</span>
              <input
                v-model="bodyVarExamples[i]"
                class="flex-1 px-2 py-1 text-xs rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12"
                :placeholder="t('DISPARADOR.templates.example')"
              />
            </div>
          </div>
        </div>

        <!-- rodapé -->
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('DISPARADOR.templates.footer')
          }}</label>
          <input
            v-model="form.footer"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            :placeholder="t('DISPARADOR.templates.footerPlaceholder')"
          />
        </div>

        <!-- botões -->
        <div>
          <div class="flex items-center justify-between mb-1">
            <label class="text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.buttons')
            }}</label>
            <button
              v-if="form.buttons.length < 3"
              type="button"
              class="text-xs text-n-brand hover:underline"
              @click="addButton"
            >
              + {{ t('DISPARADOR.templates.addButton') }}
            </button>
          </div>
          <div
            v-for="(btn, i) in form.buttons"
            :key="`btn${i}`"
            class="flex flex-wrap items-center gap-2 mb-2"
          >
            <KanbanSelect
              v-model="btn.type"
              :options="buttonTypeOptions"
              class="min-w-[150px]"
            />
            <input
              v-model="btn.text"
              class="flex-1 min-w-[120px] px-2 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              :placeholder="t('DISPARADOR.templates.buttonText')"
            />
            <input
              v-if="btn.type === 'URL'"
              v-model="btn.url"
              class="flex-1 min-w-[140px] px-2 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              :placeholder="t('DISPARADOR.urlPlaceholder')"
            />
            <input
              v-if="btn.type === 'PHONE_NUMBER'"
              v-model="btn.phone_number"
              class="flex-1 min-w-[140px] px-2 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              :placeholder="t('DISPARADOR.phonePlaceholder')"
            />
            <button
              type="button"
              class="text-n-ruby-11 hover:text-n-ruby-10"
              @click="removeButton(i)"
            >
              ✕
            </button>
          </div>
        </div>

        <div v-if="error" class="text-sm text-n-ruby-11">{{ error }}</div>

        <div class="flex items-center gap-2">
          <button
            class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
            :disabled="saving"
            @click="submit"
          >
            {{
              saving
                ? t('DISPARADOR.templates.saving')
                : t('DISPARADOR.templates.submit', { n: selected.size })
            }}
          </button>
          <button
            class="px-4 py-2 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            @click="resetForm"
          >
            {{ t('DISPARADOR.templates.clear') }}
          </button>
        </div>

        <!-- resultados por caixa -->
        <div v-if="results.length" class="mt-2 space-y-1">
          <div
            v-for="r in results"
            :key="r.inbox_id"
            class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border"
            :class="
              r.success
                ? 'border-n-teal-6 bg-n-teal-2'
                : 'border-n-ruby-6 bg-n-ruby-2'
            "
          >
            <span :class="r.success ? 'text-n-teal-11' : 'text-n-ruby-11'">{{
              r.success ? '✓' : '✕'
            }}</span>
            <span class="font-medium text-n-slate-12">{{ r.inbox_name }}</span>
            <span v-if="!r.success" class="text-xs text-n-ruby-11">{{
              r.error
            }}</span>
            <span v-else class="text-xs text-n-teal-11">{{
              t('DISPARADOR.templates.created')
            }}</span>
          </div>
        </div>
      </div>

      <!-- preview -->
      <div class="lg:col-span-1">
        <label class="block mb-1.5 text-sm font-medium text-n-slate-12">{{
          t('DISPARADOR.templates.preview')
        }}</label>
        <div class="p-3 rounded-xl bg-[#e5ddd5] dark:bg-n-solid-2">
          <div class="p-3 bg-white rounded-lg shadow-sm dark:bg-n-solid-3">
            <p
              v-if="form.header_format === 'TEXT' && previewHeader"
              class="mb-1 text-sm font-bold text-n-slate-12"
            >
              {{ previewHeader }}
            </p>
            <div
              v-else-if="isMediaHeader"
              class="flex items-center justify-center h-24 mb-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-10"
            >
              <img
                v-if="form.header_media_preview"
                :src="form.header_media_preview"
                class="object-cover w-full h-24 rounded-md"
              />
              <span v-else>{{ form.header_format }}</span>
            </div>
            <p class="text-sm whitespace-pre-wrap text-n-slate-12">
              {{ previewBody || t('DISPARADOR.templates.bodyPlaceholder') }}
            </p>
            <p v-if="form.footer" class="mt-1 text-xs text-n-slate-10">
              {{ form.footer }}
            </p>
            <div
              v-if="form.buttons.length"
              class="pt-2 mt-2 space-y-1 border-t border-n-weak"
            >
              <p
                v-for="(btn, i) in form.buttons"
                :key="`pb${i}`"
                class="text-sm text-center text-n-brand"
              >
                {{ btn.text || t('DISPARADOR.templates.buttonText') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
      </div>
      <!-- /scroll -->
    </div>
  </div>
</template>
